import { useState, useEffect, useCallback, useRef } from "react";

interface SSOPopupResult {
  success: boolean;
  ssoToken?: string;
  error?: string;
}

interface UseSSOPopupReturn {
  openSSOPopup: (redirectUrl: string) => void;
  isPopupOpen: boolean;
  result: SSOPopupResult | null;
  clearResult: () => void;
}

/**
 * Hook to handle SSO authentication in a popup window.
 * Used when the app is running inside an iframe (e.g., Lovable preview)
 * where direct page redirects to OIDC providers fail due to X-Frame-Options.
 */
export function useSSOPopup(): UseSSOPopupReturn {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [result, setResult] = useState<SSOPopupResult | null>(null);
  const popupRef = useRef<Window | null>(null);
  const pollIntervalRef = useRef<number | null>(null);

  const clearResult = useCallback(() => {
    setResult(null);
  }, []);

  // Listen for postMessage from popup
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Validate message origin - accept from same origin or our callback page
      if (event.data?.type === 'SSO_CALLBACK_RESULT') {
        const { success, ssoToken, error } = event.data;
        setResult({ success, ssoToken, error });
        setIsPopupOpen(false);
        
        // Close popup if still open
        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.close();
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Poll for popup close (fallback for cross-origin issues)
  useEffect(() => {
    if (isPopupOpen && popupRef.current) {
      pollIntervalRef.current = window.setInterval(() => {
        if (popupRef.current?.closed) {
          setIsPopupOpen(false);
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
        }
      }, 500);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [isPopupOpen]);

  // Also listen for localStorage changes (alternative to postMessage)
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'sso_callback_result' && event.newValue) {
        try {
          const data = JSON.parse(event.newValue);
          setResult(data);
          setIsPopupOpen(false);
          
          // Clean up
          localStorage.removeItem('sso_callback_result');
          
          if (popupRef.current && !popupRef.current.closed) {
            popupRef.current.close();
          }
        } catch (e) {
          console.error('Failed to parse SSO callback result:', e);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const openSSOPopup = useCallback((redirectUrl: string) => {
    // Calculate popup position (centered)
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    // Store popup mode flag for the callback to detect
    sessionStorage.setItem('sso_popup_mode', 'true');

    // Open popup
    const popup = window.open(
      redirectUrl,
      'sso_popup',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );

    if (popup) {
      popupRef.current = popup;
      setIsPopupOpen(true);
      popup.focus();
    } else {
      // Popup blocked - fall back to redirect
      console.warn('Popup was blocked, falling back to redirect');
      window.location.href = redirectUrl;
    }
  }, []);

  return {
    openSSOPopup,
    isPopupOpen,
    result,
    clearResult,
  };
}

/**
 * Check if the current window is inside an iframe
 */
export function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch (e) {
    // If we can't access window.top due to cross-origin, we're in an iframe
    return true;
  }
}
